/**
 * Client-side crypto for "Secure Fields" — the org-wide-password-derived
 * AES-GCM scheme described in plan `create-a-plan-for-ticklish-feigenbaum.md`.
 *
 * Threat model: backend (Cloudflare Workers + D1) is treated as untrusted for
 * the plaintext of these fields. Only the client ever sees them in the clear.
 *
 * Stored format for every encrypted blob:
 *   sf1:<base64url(iv)>:<base64url(ciphertext_with_authtag)>
 *
 * - `sf1` is a version marker; lets us evolve the format later without
 *   ambiguity, and lets `isEncryptedBlob()` distinguish legacy plaintext
 *   from ciphertext during lazy re-encryption.
 * - `iv` is a fresh 12-byte random nonce per encryption.
 * - `ciphertext_with_authtag` is what `@noble/ciphers/aes` `gcm()` returns.
 *
 * The KDF is PBKDF2-SHA-256 at 600k iterations (OWASP 2023). We persist the
 * iter count on the org row so we can raise it later without breaking old
 * verifiers — the client always derives at whatever `iters` value the org's
 * verifier was created with.
 */

import { gcm } from '@noble/ciphers/aes';
import { pbkdf2Async } from '@noble/hashes/pbkdf2';
import { sha256 } from '@noble/hashes/sha2';

export const SF_PREFIX = 'sf1:';
export const KDF_ITERS_DEFAULT = 600_000;
export const KEY_LEN = 32; // AES-256
export const IV_LEN = 12;  // GCM standard
export const SALT_LEN = 16;

const enc = new TextEncoder();
const dec = new TextDecoder();

function toBase64Url(bytes: Uint8Array): string {
	let bin = '';
	for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
	const b64 = (typeof btoa === 'function' ? btoa(bin) : Buffer.from(bin, 'binary').toString('base64'));
	return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function fromBase64Url(s: string): Uint8Array {
	const b64 = s.replace(/-/g, '+').replace(/_/g, '/') + '==='.slice((s.length + 3) % 4);
	const bin = typeof atob === 'function' ? atob(b64) : Buffer.from(b64, 'base64').toString('binary');
	const out = new Uint8Array(bin.length);
	for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
	return out;
}

function randomBytes(n: number): Uint8Array {
	const buf = new Uint8Array(n);
	crypto.getRandomValues(buf);
	return buf;
}

/** Generate a fresh 16-byte salt for first-time password setup. */
export function generateSalt(): Uint8Array {
	return randomBytes(SALT_LEN);
}

/** Encode a Uint8Array salt as the base64url string we persist on the org row. */
export function encodeSalt(salt: Uint8Array): string {
	return toBase64Url(salt);
}

export function decodeSalt(s: string): Uint8Array {
	return fromBase64Url(s);
}

/**
 * Derive the 32-byte AES key from the password. ~half a second on a modern
 * laptop at 600k iters — slow on purpose. Call once per session and cache
 * the result in the unlock context.
 */
export async function deriveKey(
	password: string,
	salt: Uint8Array,
	iters: number = KDF_ITERS_DEFAULT,
): Promise<Uint8Array> {
	return await pbkdf2Async(sha256, enc.encode(password), salt, { c: iters, dkLen: KEY_LEN });
}

/** Encrypt a UTF-8 string. Output is the `sf1:iv:ct` blob format. */
export function encryptString(key: Uint8Array, plaintext: string): string {
	const iv = randomBytes(IV_LEN);
	const ct = gcm(key, iv).encrypt(enc.encode(plaintext));
	return `${SF_PREFIX}${toBase64Url(iv)}:${toBase64Url(ct)}`;
}

/**
 * Decrypt an `sf1:iv:ct` blob back to its UTF-8 plaintext. Throws if the key
 * is wrong, the blob is tampered, or the format is malformed.
 */
export function decryptString(key: Uint8Array, blob: string): string {
	if (!blob.startsWith(SF_PREFIX)) throw new Error('not an encrypted blob');
	const rest = blob.slice(SF_PREFIX.length);
	const colon = rest.indexOf(':');
	if (colon === -1) throw new Error('malformed blob');
	const iv = fromBase64Url(rest.slice(0, colon));
	const ct = fromBase64Url(rest.slice(colon + 1));
	if (iv.length !== IV_LEN) throw new Error('bad iv');
	const pt = gcm(key, iv).decrypt(ct);
	return dec.decode(pt);
}

/** Cheap shape check — does NOT prove the blob decrypts. */
export function isEncryptedBlob(s: string | null | undefined): boolean {
	return typeof s === 'string' && s.startsWith(SF_PREFIX);
}

/**
 * Decrypt-or-empty helper for render paths. Display/print pages map ciphertext
 * → plaintext across a list of rows; a tampered or stale blob shouldn't crash
 * the page, just render as empty. Callers handle the empty case visually.
 */
export function safeDecrypt(decrypt: (blob: string) => string, blob: string): string {
	try { return decrypt(blob); } catch { return ''; }
}

/**
 * Build the verifier ciphertext stored on the org row. The decrypted payload
 * is JSON pinned to the org id so a verifier from another tenant can't be
 * silently substituted — the decrypt-then-check-org step refuses it.
 */
export function buildVerifier(key: Uint8Array, orgId: string): string {
	const payload = JSON.stringify({ org: orgId, v: 1, issued: Date.now() });
	return encryptString(key, payload);
}

/** Returns true iff the verifier decrypts AND the decoded org matches. */
export function checkVerifier(key: Uint8Array, blob: string, orgId: string): boolean {
	try {
		const payload = JSON.parse(decryptString(key, blob)) as { org?: unknown };
		return typeof payload.org === 'string' && payload.org === orgId;
	} catch {
		return false;
	}
}

/**
 * Convenience: zero out a key buffer when the session locks. Doesn't help on
 * platforms where the engine might've copied the buffer (Hermes, V8), but
 * it's a defense-in-depth gesture that costs nothing.
 */
export function zeroKey(key: Uint8Array): void {
	key.fill(0);
}
